import React, { useState, useEffect } from 'react';
import { scaleLinear } from '@visx/scale';
import { Bar } from '@visx/shape';
import { useTooltip, TooltipWithBounds } from '@visx/tooltip';
import { AxisBottom } from '@visx/axis';
import Truncate from 'react-truncate';

import { thousandFormat2 } from '../../../../Utils/Functions';
import {
  positiveActiveColor,
  negativeActiveColor,
} from '../../../../Assets/js/constant';

const MIN_TICK_SIZE = 1000;
const TARGET_TICK_COUNT = 4;
const MIN_ZERO_LABEL_RATIO = 0.04;
const MIN_SIDE_LABEL_RATIO = 0.08;

const getNiceTickStep = (rawStep) => {
  if (!Number.isFinite(rawStep) || rawStep <= 0) return MIN_TICK_SIZE;

  const minStep = Math.max(rawStep, MIN_TICK_SIZE);
  const exponent = Math.floor(Math.log10(minStep));
  const base = 10 ** exponent;
  const fraction = minStep / base;

  if (fraction <= 1) return base;
  if (fraction <= 2) return 2 * base;
  if (fraction <= 5) return 5 * base;
  return 10 * base;
};

const getAxisSettings = (rawMinValue, rawMaxValue) => {
  let minValue = Number.isFinite(rawMinValue) ? rawMinValue : 0;
  let maxValue = Number.isFinite(rawMaxValue) ? rawMaxValue : 1;

  if (minValue > 0) minValue = 0;
  if (maxValue < 0) maxValue = 0;

  const negativeStep = getNiceTickStep(Math.abs(minValue) / TARGET_TICK_COUNT);
  const positiveStep = getNiceTickStep(Math.abs(maxValue) / TARGET_TICK_COUNT);

  const minDomain = minValue < 0
    ? -Math.ceil(Math.abs(minValue) / negativeStep) * negativeStep
    : 0;
  let maxDomain = maxValue > 0
    ? Math.ceil(maxValue / positiveStep) * positiveStep
    : 0;

  if (minDomain === maxDomain) maxDomain = minDomain + positiveStep;

  const domainSpan = maxDomain - minDomain;
  const hasPositiveAndNegativeValues = minDomain < 0 && maxDomain > 0;

  /*
   * When one side of the axis is tiny compared with the other, its ticks are
   * squeezed into just a few pixels around zero. That is what created the
   * overlapping label stack on the Balance Sheet liability chart.
   */
  const suppressNegativeTicks =
    hasPositiveAndNegativeValues &&
    Math.abs(minDomain) / domainSpan < MIN_SIDE_LABEL_RATIO;
  const suppressPositiveTicks =
    hasPositiveAndNegativeValues &&
    Math.abs(maxDomain) / domainSpan < MIN_SIDE_LABEL_RATIO;
  const hideZeroTickLabel =
    hasPositiveAndNegativeValues &&
    !suppressNegativeTicks &&
    !suppressPositiveTicks &&
    Math.min(Math.abs(minDomain), Math.abs(maxDomain)) / domainSpan < MIN_ZERO_LABEL_RATIO;

  const tickValues = [];
  const addTick = (value) => {
    const normalisedValue = Object.is(value, -0) ? 0 : value;
    if (!tickValues.includes(normalisedValue)) tickValues.push(normalisedValue);
  };

  if (minDomain < 0 && !suppressNegativeTicks) {
    if (hasPositiveAndNegativeValues) {
      addTick(minDomain);
    } else {
      for (let value = minDomain; value < 0; value += negativeStep) {
        addTick(value);
      }
    }
  }

  addTick(0);

  if (maxDomain > 0 && !suppressPositiveTicks) {
    for (let value = positiveStep; value <= maxDomain; value += positiveStep) {
      addTick(value);
    }
  }

  return {
    minValue: minDomain,
    maxValue: maxDomain,
    tickValues,
    hideZeroTickLabel,
  };
};

function TopTableChart({ collapsableData, width }) {
  const {
    tooltipData,
    tooltipLeft,
    tooltipTop,
    tooltipOpen,
    showTooltip,
    hideTooltip,
  } = useTooltip();

  const [chartData, setChartData] = useState([]);
  const [openRows, setOpenRows] = useState([]);
  const [hoverBar, setHoverBar] = useState(null);

  useEffect(() => {
    prepareData();
  }, [collapsableData]);

  const prepareData = () => {
    let newChartData = [];
    if (collapsableData) {
      Object.keys(collapsableData).forEach((key) => {
        if (key !== 'Total Assets') {
          Object.keys(collapsableData[key]).forEach((pkey) => {
            if (pkey !== 'closing' && pkey !== 'opening' && pkey !== 'last') {
              let parentData = {
                name: pkey,
                opening: collapsableData[key][pkey].opening,
                closing: collapsableData[key][pkey].closing,
                subtasks: [],
              };
              Object.keys(collapsableData[key][pkey]).forEach(
                (ckey) => {
                  if (
                    ckey !== 'closing' &&
                    ckey !== 'opening' &&
                    ckey !== 'last'
                  ) {
                    parentData.subtasks.push({
                      name: ckey,
                      opening:
                        collapsableData[key][pkey][ckey].opening,
                      closing:
                        collapsableData[key][pkey][ckey].closing,
                    });
                  }
                },
              );
              newChartData.push(parentData);
            }
          });
        }
      });
    }
    setChartData(newChartData);
  };

  const handleToggleRow = (index) => {
    const currentIndex = openRows.indexOf(index);
    const newChecked = [...openRows];

    if (currentIndex === -1) newChecked.push(index);
    else newChecked.splice(currentIndex, 1);

    setOpenRows(newChecked);
  };

  const getColor = (data) => {
    return data >= 0 ? positiveActiveColor : negativeActiveColor;
  };

  const handleShowTooltip = (event, key, opening, closing, isBar = true) => {
    let tooltipData, top, left;

    top = event.clientY - 50;
    left = event.clientX - 100;

    tooltipData = {
      key,
      opening,
      closing,
      isBar
    };

    if (isBar) {
      if (hoverBar) hoverBar.classList.remove('barHover');
      event.target.classList.add('barHover');
      setHoverBar(event.target);
    }

    showTooltip({
      tooltipData,
      tooltipTop: top,
      tooltipLeft: left,
    });
  };

  const handleHideTooltip = () => {
    if (hoverBar) {
      hoverBar.classList.remove('barHover');
      setHoverBar(null);
    }
    hideTooltip();
  };

  let minValue = Math.min(...chartData.map((o) => o.closing));
  let maxValue = Math.max(...chartData.map((o) => o.closing));

  if (openRows.length > 0) {
    openRows.forEach((index) => {
      let childMinValue = Math.min(
        ...chartData[index].subtasks.map((o) => o.closing),
      );
      minValue = Math.min(minValue, childMinValue);
      let childMaxValue = Math.max(
        ...chartData[index].subtasks.map((o) => o.closing),
      );
      maxValue = Math.max(maxValue, childMaxValue);
    });
  }

  const axisSettings = getAxisSettings(minValue, maxValue);
  minValue = axisSettings.minValue;
  maxValue = axisSettings.maxValue;

  const xScale = scaleLinear({
    domain: [minValue, maxValue],
    range: [0, width * 0.55 - 1],
  });

  let tooltipTimeout;

  return (
    <>
      {chartData.map((data, index) => {
        let barX = xScale(0);
        let barWidth = xScale(data.closing) - xScale(0);

        if (data.closing < 0) {
          barX = xScale(data.closing);
          barWidth = xScale(0) - xScale(data.closing);
        }

        return (
          <div
            key={index}
            style={{
              fontSize: 12,
              borderTop: 'solid 1px #a9a9a9',
              borderBottom:
                index === chartData.length - 1 ? 'solid 1px #a9a9a9' : 'none',
              height: openRows.indexOf(index) === -1 ? 21.25 : 'unset',
            }}
          >
            <div
              style={{
                float: 'left',
                marginTop: 2,
                width:
                  openRows.indexOf(index) === -1
                    ? width * 0.45
                    : width * 0.45 * 0.4,
                whiteSpace: data.subtasks.length === 1 ? 'nowrap' : 'unset',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}
              onClick={() => handleToggleRow(index)}
            >
              {data.name}
            </div>
            {openRows.indexOf(index) > -1 ? (
              <div style={{ display: 'flow-root' }}>
                {data.subtasks.map((cdata, cIndex) => {
                  let cbarX = xScale(0);
                  let cbarWidth = xScale(cdata.closing) - xScale(0);

                  if (cdata.closing < 0) {
                    cbarX = xScale(cdata.closing);
                    cbarWidth = xScale(0) - xScale(cdata.closing);
                  }

                  return (
                    <div
                      key={cIndex}
                      style={{
                        height: 21,
                        borderBottom:
                          cIndex !== data.subtasks.length - 1
                            ? 'solid 1px #a9a9a9'
                            : 'none',
                      }}
                    >
                      <div
                        style={{
                          float: 'left',
                          width: width * 0.45 * 0.6,
                          marginTop: 2,
                        }}
                      >
                        <Truncate>
                          {cdata.name}
                        </Truncate>
                      </div>
                      <div
                        style={{
                          width: width * 0.55 - 1,
                          float: 'left',
                          height: 21,
                        }}
                      >
                        <svg width={width * 0.55 - 1} height={19}>
                          <rect
                            width={width * 0.55 - 1}
                            height={19}
                            fill="white"
                          />
                          <Bar
                            x={cbarX}
                            y={1}
                            width={cbarWidth}
                            height={18}
                            fill={getColor(cdata.closing)}
                            onMouseMove={(event) => {
                              if (tooltipTimeout) clearTimeout(tooltipTimeout);
                              handleShowTooltip(
                                event,
                                cdata.name,
                                cdata.opening,
                                cdata.closing,
                              );
                            }}
                            onMouseLeave={() => {
                              tooltipTimeout = setTimeout(() => {
                                handleHideTooltip();
                              }, 300);
                            }}
                            onTouchMove={(event) => {
                              if (tooltipTimeout) clearTimeout(tooltipTimeout);
                              handleShowTooltip(
                                event,
                                cdata.name,
                                cdata.opening,
                                cdata.closing,
                              );
                            }}
                            onTouchEnd={() => {
                              tooltipTimeout = setTimeout(() => {
                                handleHideTooltip();
                              }, 300);
                            }}
                          />
                        </svg>
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div
                style={{
                  width: width * 0.55 - 1,
                  float: 'left',
                  height: 21,
                }}
              >
                <svg width={width * 0.55 - 1} height={19}>
                  <rect
                    width={width * 0.55 - 1}
                    height={19}
                    fill="white"
                  />
                  <Bar
                    x={barX}
                    y={1}
                    width={barWidth}
                    height={18}
                    fill={getColor(data.closing)}
                    onMouseMove={(event) => {
                      if (tooltipTimeout) clearTimeout(tooltipTimeout);
                      handleShowTooltip(event, data.name, data.opening, data.closing);
                    }}
                    onMouseLeave={() => {
                      tooltipTimeout = setTimeout(() => {
                        handleHideTooltip();
                      }, 300);
                    }}
                    onTouchMove={(event) => {
                      if (tooltipTimeout) clearTimeout(tooltipTimeout);
                      handleShowTooltip(event, data.name, data.opening, data.closing);
                    }}
                    onTouchEnd={() => {
                      tooltipTimeout = setTimeout(() => {
                        handleHideTooltip();
                      }, 300);
                    }}
                  />
                </svg>
              </div>
            )}
          </div>
        );
      })}

      {chartData.length > 0 && (
        <svg
          width={width + 20}
          height={26}
          style={{ overflow: 'visible' }}
        >
          <AxisBottom
            scale={xScale}
            top={0}
            left={width * 0.45}
            hideAxisLine={true}
            stroke="black"
            tickValues={axisSettings.tickValues}
            tickStroke="#a9a9a9"
            tickFormat={(value) => value / 1000}
            tickLabelProps={() => ({
              fill: 'black',
              fontSize: 12,
              textAnchor: 'middle',
              dy: '0.2em',
            })}
            tickComponent={({ formattedValue, ...tickProps }) => {
              if (
                axisSettings.hideZeroTickLabel &&
                Number(formattedValue) === 0
              ) {
                return <text {...tickProps} />;
              }

              return (
                <text {...tickProps}>
                  $
                  {Math.abs(formattedValue) < 1000
                    ? `${formattedValue}K`
                    : `${formattedValue / 1000}M`}
                </text>
              );
            }}
          />
        </svg>
      )}

      {tooltipOpen && (
        <TooltipWithBounds top={tooltipTop} left={tooltipLeft}>
          <div className="color-black">
            <div className="pdv-5">
              <strong>{tooltipData.key}&nbsp; &nbsp; </strong>
            </div>
            <div className="ft-12">
              Open Balance: {thousandFormat2(tooltipData.opening)}
            </div>
            <div className="ft-12">
              Close Balance: {thousandFormat2(tooltipData.closing)}
            </div>
            <div className="ft-12">
              Change:{' '}
              {thousandFormat2(
                tooltipData.closing - tooltipData.opening,
              )}
            </div>
          </div>
        </TooltipWithBounds>
      )}
    </>
  );
}

export default TopTableChart;
